/**
 * Del Toro FAQ Page JavaScript
 * Handles category navigation, smooth scrolling, back-to-top, and FAQ interactions
 */

(function() {
    'use strict';
    
    // FAQ Management System
    const faqManager = {
        isManualNavigation: false, // Flag to track manual clicks
        
        init: function() {
            this.setupCategoryNavigation();
            this.setupFAQToggles();
            this.setupBackToTop();
            this.setupStickyNavigation(); // Re-enabled with better conflict prevention
            this.setupSmoothScrolling();
            this.setupMobileNavigation();
            this.handleURLAnchors();
            
            // Performance monitoring
            if (window.performance && window.performance.mark) {
                window.performance.mark('faq-js-loaded');
            }
        },
        
        setupCategoryNavigation: function() {
            const tabs = document.querySelectorAll('.dt-category-tab');
            const categories = document.querySelectorAll('.dt-faq-category');
            
            tabs.forEach(tab => {
                tab.addEventListener('click', (e) => {
                    e.preventDefault();
                    
                    // Set manual navigation flag to prevent observer interference
                    this.isManualNavigation = true;
                    
                    // Get target category
                    const target = tab.getAttribute('href').substring(1);
                    const targetCategory = document.getElementById(target);
                    
                    if (targetCategory) {
                        // Update active tab immediately
                        this.updateActiveTab(target);
                        
                        // Smooth scroll to category
                        this.scrollToElement(targetCategory, 120); // Account for sticky nav
                        
                        // Update URL without triggering page reload
                        if (history.pushState) {
                            history.pushState(null, null, `#${target}`);
                        }
                        
                        // Reset manual navigation flag after scroll completes
                        setTimeout(() => {
                            this.isManualNavigation = false;
                        }, 1000); // Allow time for scroll animation
                    }
                });
            });
        },
        
        setupFAQToggles: function() {
            const faqToggles = document.querySelectorAll('.dt-faq-toggle');
            
            faqToggles.forEach(toggle => {
                // Set up click handler for the label
                const label = toggle.nextElementSibling;
                if (label && label.classList.contains('dt-faq-question')) {
                    label.addEventListener('click', (e) => {
                        e.preventDefault(); // Prevent default label behavior
                        
                        // Close other FAQ items in same category (accordion behavior)
                        const category = toggle.closest('.dt-faq-category');
                        if (category) {
                            const otherToggles = category.querySelectorAll('.dt-faq-toggle');
                            otherToggles.forEach(otherToggle => {
                                if (otherToggle !== toggle && otherToggle.checked) {
                                    otherToggle.checked = false;
                                }
                            });
                        }
                        
                        // Toggle current FAQ
                        toggle.checked = !toggle.checked;
                        
                        // Track FAQ interaction for analytics
                        if (typeof gtag !== 'undefined') {
                            gtag('event', 'faq_toggle', {
                                'faq_question': label.textContent.trim(),
                                'action': toggle.checked ? 'open' : 'close'
                            });
                        }
                    });
                }
            });
        },
        
        setupStickyNavigation: function() {
            const stickyNav = document.querySelector('.dt-category-nav');
            if (!stickyNav) return;
            
            // Wait for page to fully load to get accurate position
            setTimeout(() => {
                // Get the initial position of the navigation
                const navTop = stickyNav.offsetTop;
                let isSticky = false;
                
                // Create a placeholder to prevent content jump
                const placeholder = document.createElement('div');
                placeholder.className = 'dt-nav-placeholder';
                placeholder.style.cssText = `
                    height: ${stickyNav.offsetHeight}px;
                    display: none;
                    visibility: hidden;
                `;
                stickyNav.parentNode.insertBefore(placeholder, stickyNav.nextSibling);
                
                const handleScroll = () => {
                    try {
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        
                        if (scrollTop >= navTop && !isSticky) {
                            // Make sticky with higher specificity to override theme
                            stickyNav.style.cssText = `
                                position: fixed !important;
                                top: 0 !important;
                                left: 0 !important;
                                right: 0 !important;
                                width: 100% !important;
                                z-index: 1001 !important;
                                background: white !important;
                                border-bottom: 1px solid #E5E5E5 !important;
                                padding: 20px 0 !important;
                                margin: 0 !important;
                                box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
                            `;
                            placeholder.style.display = 'block';
                            isSticky = true;
                        } else if (scrollTop < navTop && isSticky) {
                            // Remove sticky
                            stickyNav.style.cssText = '';
                            placeholder.style.display = 'none';
                            isSticky = false;
                        }
                    } catch (e) {
                        // Prevent any errors from breaking the page
                        console.warn('Sticky navigation error:', e);
                    }
                };
                
                // Use throttled scroll handler for better performance
                let ticking = false;
                const throttledScroll = () => {
                    if (!ticking) {
                        requestAnimationFrame(() => {
                            handleScroll();
                            ticking = false;
                        });
                        ticking = true;
                    }
                };
                
                window.addEventListener('scroll', throttledScroll, { passive: true });
                window.addEventListener('resize', () => {
                    if (!isSticky) {
                        placeholder.style.height = stickyNav.offsetHeight + 'px';
                    }
                });
                
            }, 100); // Small delay to ensure accurate positioning
        },
        
        setupBackToTop: function() {
            const backToTopBtn = document.querySelector('.dt-back-to-top');
            if (!backToTopBtn) return;
            
            // Show/hide back to top button based on scroll position
            let ticking = false;
            
            const updateBackToTop = () => {
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                
                if (scrollTop > 300) {
                    backToTopBtn.classList.add('visible');
                } else {
                    backToTopBtn.classList.remove('visible');
                }
                ticking = false;
            };
            
            const requestTick = () => {
                if (!ticking) {
                    requestAnimationFrame(updateBackToTop);
                    ticking = true;
                }
            };
            
            window.addEventListener('scroll', requestTick, { passive: true });
            
            // Handle back to top click
            backToTopBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.scrollToElement(document.body, 0);
            });
        },
        
        setupSmoothScrolling: function() {
            // Handle all anchor links within the page
            const anchorLinks = document.querySelectorAll('a[href^="#"]');
            
            anchorLinks.forEach(link => {
                link.addEventListener('click', (e) => {
                    const href = link.getAttribute('href');
                    if (href === '#') return;
                    
                    e.preventDefault();
                    const target = document.querySelector(href);
                    
                    if (target) {
                        this.scrollToElement(target, 120);
                        
                        // Update URL
                        if (history.pushState) {
                            history.pushState(null, null, href);
                        }
                    }
                });
            });
        },
        
        setupMobileNavigation: function() {
            const mobileSelect = document.querySelector('.dt-mobile-category-select');
            if (!mobileSelect) return;
            
            mobileSelect.addEventListener('change', (e) => {
                const targetId = e.target.value;
                if (targetId) {
                    // Set manual navigation flag
                    this.isManualNavigation = true;
                    
                    const targetCategory = document.getElementById(targetId);
                    if (targetCategory) {
                        // Update active tab immediately
                        this.updateActiveTab(targetId);
                        
                        this.scrollToElement(targetCategory, 100);
                        
                        // Update URL
                        if (history.pushState) {
                            history.pushState(null, null, `#${targetId}`);
                        }
                        
                        // Reset manual navigation flag after scroll completes
                        setTimeout(() => {
                            this.isManualNavigation = false;
                        }, 1000);
                    }
                }
            });
        },
        
        handleURLAnchors: function() {
            // Handle page load with anchor in URL
            const hash = window.location.hash;
            if (hash) {
                setTimeout(() => {
                    const target = document.querySelector(hash);
                    if (target) {
                        this.scrollToElement(target, 120);
                        this.updateActiveTab(hash.substring(1));
                    }
                }, 100); // Small delay to ensure page is fully loaded
            }
            
            // Handle browser back/forward with anchors
            window.addEventListener('popstate', () => {
                const hash = window.location.hash;
                if (hash) {
                    const target = document.querySelector(hash);
                    if (target) {
                        this.scrollToElement(target, 120);
                        this.updateActiveTab(hash.substring(1));
                    }
                }
            });
        },
        
        updateActiveTab: function(targetId) {
            // Update active tab based on current section
            const tabs = document.querySelectorAll('.dt-category-tab');
            const mobileSelect = document.querySelector('.dt-mobile-category-select');
            
            tabs.forEach(tab => {
                const tabHref = tab.getAttribute('href').substring(1);
                if (tabHref === targetId) {
                    tab.classList.add('active');
                } else {
                    tab.classList.remove('active');
                }
            });
            
            if (mobileSelect) {
                mobileSelect.value = targetId;
            }
        },
        
        scrollToElement: function(element, offset = 0) {
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };
    
    // Search functionality (optional enhancement)
    const searchManager = {
        init: function() {
            const searchInput = document.querySelector('.dt-search-input');
            if (!searchInput) return;
            
            let searchTimeout;
            
            searchInput.addEventListener('input', (e) => {
                clearTimeout(searchTimeout);
                searchTimeout = setTimeout(() => {
                    this.performSearch(e.target.value);
                }, 300); // Debounce search
            });
        },
        
        performSearch: function(query) {
            const faqItems = document.querySelectorAll('.dt-faq-item');
            const categories = document.querySelectorAll('.dt-faq-category');
            
            if (!query.trim()) {
                // Show all items if search is empty
                faqItems.forEach(item => {
                    item.style.display = 'block';
                });
                categories.forEach(category => {
                    category.style.display = 'block';
                });
                return;
            }
            
            const searchTerm = query.toLowerCase();
            let hasVisibleItems = {};
            
            faqItems.forEach(item => {
                const question = item.querySelector('.dt-faq-question').textContent.toLowerCase();
                const answer = item.querySelector('.dt-faq-answer-content').textContent.toLowerCase();
                const category = item.closest('.dt-faq-category').id;
                
                if (question.includes(searchTerm) || answer.includes(searchTerm)) {
                    item.style.display = 'block';
                    hasVisibleItems[category] = true;
                } else {
                    item.style.display = 'none';
                }
            });
            
            // Hide categories with no visible items
            categories.forEach(category => {
                if (hasVisibleItems[category.id]) {
                    category.style.display = 'block';
                } else {
                    category.style.display = 'none';
                }
            });
            
            // Track search for analytics
            if (typeof gtag !== 'undefined') {
                gtag('event', 'faq_search', {
                    'search_term': query
                });
            }
        }
    };
    
    // Intersection Observer for active tab highlighting
    const observerManager = {
        init: function() {
            if (!('IntersectionObserver' in window)) return;
            
            const categories = document.querySelectorAll('.dt-faq-category');
            if (categories.length === 0) return;
            
            const observerOptions = {
                root: null,
                rootMargin: '-120px 0px -50% 0px', // Account for sticky nav
                threshold: 0.1
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !faqManager.isManualNavigation) {
                        const categoryId = entry.target.id;
                        if (categoryId) {
                            // Only update if this isn't a manual navigation
                            faqManager.updateActiveTab(categoryId);
                            
                            // Update URL hash without triggering page reload
                            if (history.replaceState && window.location.hash !== `#${categoryId}`) {
                                history.replaceState(null, null, `#${categoryId}`);
                            }
                        }
                    }
                });
            }, observerOptions);
            
            categories.forEach(category => {
                observer.observe(category);
            });
        }
    };
    
    // Accessibility enhancements
    const accessibilityManager = {
        init: function() {
            this.setupKeyboardNavigation();
            this.setupFocusManagement();
            this.setupARIA();
        },
        
        setupKeyboardNavigation: function() {
            // Allow Enter/Space to trigger FAQ toggles
            const faqQuestions = document.querySelectorAll('.dt-faq-question');
            
            faqQuestions.forEach(question => {
                question.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        question.click();
                    }
                });
            });
            
            // Tab navigation for category tabs
            const categoryTabs = document.querySelectorAll('.dt-category-tab');
            categoryTabs.forEach((tab, index) => {
                tab.addEventListener('keydown', (e) => {
                    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                        e.preventDefault();
                        const direction = e.key === 'ArrowRight' ? 1 : -1;
                        const nextIndex = (index + direction + categoryTabs.length) % categoryTabs.length;
                        categoryTabs[nextIndex].focus();
                        categoryTabs[nextIndex].click();
                    }
                });
            });
        },
        
        setupFocusManagement: function() {
            // Ensure proper focus management when FAQ items open/close
            const faqToggles = document.querySelectorAll('.dt-faq-toggle');
            
            faqToggles.forEach(toggle => {
                toggle.addEventListener('change', () => {
                    if (toggle.checked) {
                        const answer = toggle.parentElement.querySelector('.dt-faq-answer');
                        if (answer) {
                            // Brief delay to allow animation to start
                            setTimeout(() => {
                                answer.setAttribute('tabindex', '0');
                            }, 100);
                        }
                    }
                });
            });
        },
        
        setupARIA: function() {
            // Add ARIA attributes for better screen reader support
            const faqQuestions = document.querySelectorAll('.dt-faq-question');
            
            faqQuestions.forEach((question, index) => {
                const toggle = question.previousElementSibling;
                const answer = question.parentElement.querySelector('.dt-faq-answer');
                
                if (toggle && answer) {
                    const questionId = `faq-question-${index}`;
                    const answerId = `faq-answer-${index}`;
                    
                    question.setAttribute('id', questionId);
                    question.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
                    question.setAttribute('aria-controls', answerId);
                    question.setAttribute('role', 'button');
                    question.setAttribute('tabindex', '0');
                    
                    answer.setAttribute('id', answerId);
                    answer.setAttribute('aria-labelledby', questionId);
                    answer.setAttribute('role', 'region');
                    
                    // Update aria-expanded when toggle changes
                    toggle.addEventListener('change', () => {
                        question.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
                    });
                }
            });
        }
    };
    
    // Performance optimization
    const performanceManager = {
        init: function() {
            // Lazy load FAQ content if needed
            this.optimizeAnimations();
            this.setupPreloadHints();
        },
        
        optimizeAnimations: function() {
            // Reduce motion for users who prefer it
            if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                const style = document.createElement('style');
                style.textContent = `
                    .dt-faq-answer { transition: none !important; }
                    .dt-category-tab { transition: none !important; }
                    .dt-back-to-top { transition: none !important; }
                `;
                document.head.appendChild(style);
            }
        },
        
        setupPreloadHints: function() {
            // Preload critical resources
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => {
                    const link = document.createElement('link');
                    link.rel = 'prefetch';
                    link.href = '/pages/contact';
                    document.head.appendChild(link);
                });
            }
        }
    };
    
    // Initialize everything when DOM is ready
    document.addEventListener('DOMContentLoaded', function() {
        // Del Toro FAQ Page JavaScript initialized
        
        // Initialize all modules
        faqManager.init();
        searchManager.init();
        observerManager.init();
        accessibilityManager.init();
        performanceManager.init();
        
        // Mark initialization complete
        if (window.performance && window.performance.mark) {
            window.performance.mark('faq-initialization-complete');
        }
    });
    
    // Handle page visibility changes for performance
    document.addEventListener('visibilitychange', function() {
        if (document.visibilityState === 'visible') {
            // Page became visible - resume any paused operations
            observerManager.init();
        }
    });
    
    // Expose public API for external use if needed
    window.DelToroFAQ = {
        scrollToCategory: function(categoryId) {
            const category = document.getElementById(categoryId);
            if (category) {
                faqManager.scrollToElement(category, 120);
                faqManager.updateActiveTab(categoryId);
            }
        },
        
        openFAQ: function(faqId) {
            const faqToggle = document.querySelector(`#${faqId}`);
            if (faqToggle && !faqToggle.checked) {
                faqToggle.checked = true;
            }
        }
    };
    
})();